import { HttpEventType } from '@angular/common/http';
import { Component, computed, ElementRef, inject, input, output, signal, TemplateRef, viewChild } from '@angular/core';
import { Observable, filter, finalize, map, merge, switchMap, take, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  AbilityService,
  BtnLoaderDirective,
  DealStatus,
  DealStatusLabels,
  DocPreviewComponent,
  FileUploadService,
  NgbModalService,
  SpinnerService,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DragDropModule } from '@openng/optimus-ui/dragdrop';
import { BuyerContract } from '../../../../models/buyer-contract.model';
import {
  DealDocumentCustomTemplateState,
  DealDocumentTemplateState,
  DocumentTemplate,
  DocumentTemplateWithSelected,
  GeneratedSchedulePeDocument,
  miscellaneousDocument
} from '../../../../models/deal-document.models';
import { SalesApsService } from '../../../../services/sales-aps.service';
import { DealDocumentsService } from '@zayan-erp/sales';

@Component({
  selector: 'lib-doc-generation',
  imports: [DocPreviewComponent, SvgIconsComponent, DragDropModule, BtnLoaderDirective],
  standalone: true,
  templateUrl: './doc-generation.html',
  styleUrl: './doc-generation.scss'
})
export class DocGeneration {
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #spinnerService = inject(SpinnerService);
  readonly #toastrService = inject(ToastrService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #dealDocumentsService = inject(DealDocumentsService);
  readonly #abilityService = inject(AbilityService);

  /*inputs*/
  generationContext = input<string>('misc');
  dealId = input<string>('');
  projectId = input<string>('');
  contractDetails = input<BuyerContract>({} as BuyerContract);
  documentTemplatesInput = input<DocumentTemplateWithSelected[] | miscellaneousDocument[]>([]);

  /*outputs*/
  refreshRequested = output<void>();
  templatesChange = output<DocumentTemplateWithSelected[]>();

  /*viewchildren*/
  documentPreviewModal = viewChild<TemplateRef<unknown>>('documentPreviewModal');
  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  /*signals*/
  insertionPosition = signal<number | null>(null);
  draggedTemplateId = signal<string | null>(null);
  recentlyUpdatedTemplateId = signal<string | null>(null);
  isAddendumAttached = signal<boolean>(false);
  isLegalPlanAttached = signal<boolean>(false);
  dragStarted = signal<boolean>(false);
  #recentlyUpdatedBadgeTimeout: ReturnType<typeof setTimeout> | null = null;

  private _selectedDocument = signal<{ name: string; url: string } | null>(null);

  /*computed*/
  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  effectiveDeal = computed(() => this.#salesApsService.currentDeal());
  selectedDocument = computed(() => this._selectedDocument());
  apsTemplates = computed(() => (this.documentTemplatesInput() as DocumentTemplateWithSelected[]) ?? []);
  miscTemplates = computed(() => (this.documentTemplatesInput() as miscellaneousDocument[]) ?? []);
  isContractReady = computed(
    () => this.#salesApsService.currentDeal()?.dealStatus?.name === DealStatusLabels[DealStatus.ContractReady]
  );
  allSelected = computed(() => {
    const templates = this.apsTemplates();
    return templates.length > 0 && templates.every(t => t.isSelected);
  });

  someSelected = computed(() => {
    const templates = this.apsTemplates();
    return templates.some(t => t.isSelected) && !templates.every(t => t.isSelected);
  });

  requestUploadExternalContract(): void {
    if (!this.canWriteDeals()) return;
    this.#dealDocumentsService.requestOpenUploadExternalContractModal();
  }

  openDocumentPreviewModal(document: DocumentTemplate | miscellaneousDocument): void {
    const previewDocumentUrl = this.resolveTemplatePreviewUrl(document);
    if (previewDocumentUrl) {
      this.openPreviewModal(document.name, previewDocumentUrl);
      return;
    }
    this.#salesApsService.getDocumentFileUrl(document.id).subscribe({
      next: fileUrl => {
        if (!fileUrl) {
          this.#toastrService.error('Document preview is unavailable for this custom document');
          return;
        }
        this.openPreviewModal(document.name, fileUrl);
      },
      error: () => this.#toastrService.error('Document preview is unavailable for this custom document')
    });
  }

  generateDocumentButtonId(template: miscellaneousDocument): string {
    return `generate-document-${template.id}`;
  }

  generateMiscDocument(template: miscellaneousDocument): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.dealId();
    const documentType = template.documentType;
    if (!dealId || !documentType) {
      this.#toastrService.error('Template cannot be generated because document type is missing');
      return;
    }

    if (
      this.effectiveDeal()?.dealStatus.name === 'Pending Offer' ||
      this.effectiveDeal()?.dealStatus.name === 'Under Contract'
    ) {
      this.#dealDocumentsService
        .generateNonApsDocument(dealId, documentType, this.generateDocumentButtonId(template))
        .subscribe({
          next: () => {
            this.#salesApsService.realoadDeal.next(null);
            this.#toastrService.success(`${template.name} generated successfully`);
            this.refreshRequested.emit();
          }
        });
    } else {
      this.#toastrService.warning(
        `${template.name} could not be generated. Deal status must be in Pending offer or Under Contract`
      );
      return;
    }
  }

  generateContract(): void {
    if (!this.canWriteDeals()) return;
    const selectedTemplates = this.apsTemplates().filter(template => template.isSelected);
    if (selectedTemplates.length === 0) {
      return;
    }

    const payload = {
      documentPlacements: selectedTemplates.map(template => ({
        templateId: template.isCustom ? null : template.id,
        s3DocumentId: template.isCustom ? template.id : null,
        type: template.isCustom ? ('custom' as const) : ('template' as const)
      }))
    };

    this.#dealDocumentsService.generateDealContract(this.dealId(), payload).subscribe({
      next: () => {
        this.#salesApsService.realoadDeal.next(null);
        this.refreshRequested.emit();
      }
    });
  }

  onFileChange(event: Event): void {
    const file = this.extractFileFromInputEvent(event);
    if (file) {
      this.uploadDocument(file);
    }
  }

  uploadDocument(file: File): void {
    if (!this.canWriteDeals()) return;
    if (!file) {
      this.#toastrService.warning('Please select a file to upload');
      return;
    }

    this.uploadFileToS3(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService
            .getPresignedUrlFromS3Key(awsData.s3Key, file.name)
            .pipe(map(directUrl => ({ ...awsData, directUrl })))
        ),
        tap(awsData => {
          const newDocument: DocumentTemplateWithSelected = {
            id: awsData.documentId,
            projectId: this.projectId(),
            name: file.name,
            s3Document: {
              id: awsData.documentId,
              s3Key: awsData.s3Key,
              directUrl: awsData.directUrl
            },
            templateVariable: { id: '', name: '' },
            sequence: this.apsTemplates().length + 1,
            isSelected: true,
            isCustom: true
          };

          const currentTemplates = [...this.apsTemplates()];
          const position = this.insertionPosition();
          if (position !== null) {
            currentTemplates.splice(position, 0, newDocument);
          } else {
            currentTemplates.push(newDocument);
          }
          this.commitTemplates(currentTemplates);
          this.insertionPosition.set(null);
        })
      )
      .subscribe(() => this.#spinnerService.handleSpinnerStop());
  }

  fetchAddendums(afterTemplateId: string): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.dealId();
    if (!dealId) {
      return;
    }

    this.#salesApsService.generateSchedulePeDocument(dealId).subscribe({
      next: response => {
        const payload = response as GeneratedSchedulePeDocument;
        const documentId = payload.documentId ?? payload.DocumentId;
        const documentName = payload.documentName ?? payload.DocumentName;
        const templateUrl = payload.documentUrl ?? '';

        if (!documentId || !documentName) {
          this.#toastrService.error('Could not fetch addendum document details');
          return;
        }

        const fetchedTemplate: DocumentTemplateWithSelected = {
          id: documentId,
          projectId: this.projectId(),
          name: documentName,
          s3Document: { id: documentId, s3Key: '', directUrl: templateUrl },
          templateVariable: { id: '', name: '' },
          sequence: 0,
          isSelected: true,
          isCustom: true,
          isPositionLocked: false
        };

        const templatesWithoutFetchedDoc = this.apsTemplates().filter(template => template.id !== documentId);
        const clickedTemplateIndex = templatesWithoutFetchedDoc.findIndex(template => template.id === afterTemplateId);
        const insertIndex = clickedTemplateIndex >= 0 ? clickedTemplateIndex + 1 : templatesWithoutFetchedDoc.length;
        const nextTemplates = [...templatesWithoutFetchedDoc];
        nextTemplates.splice(insertIndex, 0, fetchedTemplate);

        this.commitTemplates(nextTemplates);
        this.showRecentlyUpdatedBadge(documentId);
        this.isAddendumAttached.set(true);
      }
    });
  }

  insertLegalPlan(position: number): void {
    if (!this.canWriteDeals()) return;
    const lotId = this.effectiveDeal()?.lot?.id ?? '';
    this.#salesApsService.generateStampedLegalPlan(lotId).subscribe({
      next: response => {
        const documentId = response.documentId;
        const documentName = response.documentName || 'Stamped Legal Plan.pdf';
        const templateUrl = response.documentUrl ?? '';

        if (!documentId) {
          this.#toastrService.error('Could not generate legal plan document');
          return;
        }

        const legalPlanTemplate: DocumentTemplateWithSelected = {
          id: documentId,
          projectId: this.projectId(),
          name: documentName,
          s3Document: { id: documentId, s3Key: '', directUrl: templateUrl },
          templateVariable: { id: '', name: '' },
          sequence: 0,
          isSelected: true,
          isCustom: true,
          isPositionLocked: false
        };

        const nextTemplates = [...this.apsTemplates()];
        nextTemplates.splice(position, 0, legalPlanTemplate);
        this.commitTemplates(nextTemplates);
        this.showRecentlyUpdatedBadge(documentId);
        this.isLegalPlanAttached.set(true);
      }
    });
  }

  toggleDocumentSelection(id: string): void {
    if (!this.canWriteDeals()) return;
    this.updateTemplates(templates => templates.map(t => (t.id === id ? { ...t, isSelected: !t.isSelected } : t)));
  }

  toggleAllDocuments(event: Event): void {
    if (!this.canWriteDeals()) return;
    const isChecked = (event.target as HTMLInputElement).checked;
    this.updateTemplates(templates => templates.map(t => ({ ...t, isSelected: isChecked })));
  }

  deleteDocument(documentId: string): void {
    if (!this.canWriteDeals()) return;
    this.updateTemplates(templates => templates.filter(template => template.id !== documentId));
  }

  onTemplateDragStart(template: DocumentTemplateWithSelected): void {
    if (!this.canWriteDeals()) return;
    this.dragStarted.set(true);
    this.draggedTemplateId.set(template.id);
  }

  onTemplateDragEnd(): void {
    this.dragStarted.set(false);
    this.draggedTemplateId.set(null);
  }

  onTemplateDrop(targetIndex: number): void {
    if (!this.canWriteDeals()) return;
    const draggedId = this.draggedTemplateId();
    const currentTemplates = this.apsTemplates();
    const sourceIndex = currentTemplates.findIndex(template => template.id === draggedId);
    if (sourceIndex === -1) {
      this.draggedTemplateId.set(null);
      return;
    }

    const draggedTemplate = currentTemplates[sourceIndex];
    if (this.isTemplatePositionLocked(draggedTemplate)) {
      this.#toastrService.warning('This document position is locked and cannot be reordered.');
      this.draggedTemplateId.set(null);
      return;
    }

    const normalizedTargetIndex = this.normalizeDropIndex(sourceIndex, targetIndex, currentTemplates.length);
    if (normalizedTargetIndex === sourceIndex) {
      this.draggedTemplateId.set(null);
      return;
    }

    const reorderedTemplates = this.moveTemplate(currentTemplates, sourceIndex, normalizedTargetIndex);
    if (this.hasLockedTemplatePositionChanged(currentTemplates, reorderedTemplates)) {
      this.#toastrService.warning('Locked documents must stay in their current positions.');
      this.draggedTemplateId.set(null);
      return;
    }

    this.commitTemplates(reorderedTemplates);
    this.showRecentlyUpdatedBadge(draggedTemplate.id);
    this.draggedTemplateId.set(null);
  }

  isTemplatePositionLocked(template: DocumentTemplateWithSelected): boolean {
    return Boolean(template.isPositionLocked);
  }

  private commitTemplates(templates: DocumentTemplateWithSelected[]): void {
    const nextTemplateState = this.buildTemplateState(templates);
    const orderedTemplates = this.applyTemplateStateOrder(templates, nextTemplateState);
    const resequencedTemplates = orderedTemplates.map((template, index) => ({
      ...template,
      sequence: index + 1
    }));

    this.templatesChange.emit(resequencedTemplates);
    const dealId = this.dealId();
    if (dealId) {
      this.#dealDocumentsService.setDealDocumentTemplateState(dealId, nextTemplateState);
    }
  }

  private updateTemplates(
    updater: (templates: DocumentTemplateWithSelected[]) => DocumentTemplateWithSelected[]
  ): void {
    this.commitTemplates(updater(this.apsTemplates()));
  }

  private buildTemplateState(templates: DocumentTemplateWithSelected[]): DealDocumentTemplateState {
    const customTemplates: DealDocumentCustomTemplateState[] = [];
    let currentSystemTemplateId: string | null = null;

    for (const template of templates) {
      if (!template.isCustom) {
        currentSystemTemplateId = template.id;
        continue;
      }
      customTemplates.push({
        customDocumentId: template.id,
        customDocumentName: template.name,
        anchorSystemTemplateId: currentSystemTemplateId
      });
    }

    return {
      orderedTemplateIds: templates.map(template => template.id),
      customTemplates
    };
  }

  private applyTemplateStateOrder(
    templates: DocumentTemplateWithSelected[],
    state: DealDocumentTemplateState
  ): DocumentTemplateWithSelected[] {
    const templateById = new Map(templates.map(template => [template.id, template]));
    const orderedTemplatesFromState: DocumentTemplateWithSelected[] = [];
    const consumedTemplateIds = new Set<string>();

    for (const templateId of state.orderedTemplateIds ?? []) {
      const template = templateById.get(templateId);
      if (!template) {
        continue;
      }
      orderedTemplatesFromState.push(template);
      consumedTemplateIds.add(templateId);
    }

    for (const template of templates) {
      if (!consumedTemplateIds.has(template.id)) {
        orderedTemplatesFromState.push(template);
      }
    }

    const orderedSystemTemplates = orderedTemplatesFromState.filter(template => !template.isCustom);
    const systemTemplateIds = new Set(orderedSystemTemplates.map(template => template.id));
    const customTemplatesByAnchor = new Map<string, DocumentTemplateWithSelected[]>();
    const unanchoredCustomTemplates: DocumentTemplateWithSelected[] = [];
    const consumedCustomIds = new Set<string>();

    for (const customState of state.customTemplates) {
      const customTemplate = templateById.get(customState.customDocumentId);
      if (!customTemplate?.isCustom) {
        continue;
      }
      consumedCustomIds.add(customTemplate.id);
      const anchorId = customState.anchorSystemTemplateId;
      if (anchorId && systemTemplateIds.has(anchorId)) {
        const anchoredTemplates = customTemplatesByAnchor.get(anchorId) ?? [];
        anchoredTemplates.push(customTemplate);
        customTemplatesByAnchor.set(anchorId, anchoredTemplates);
      } else {
        unanchoredCustomTemplates.push(customTemplate);
      }
    }

    for (const template of orderedTemplatesFromState) {
      if (template.isCustom && !consumedCustomIds.has(template.id)) {
        unanchoredCustomTemplates.push(template);
      }
    }

    const orderedTemplates: DocumentTemplateWithSelected[] = [];
    for (const systemTemplate of orderedSystemTemplates) {
      orderedTemplates.push(systemTemplate);
      orderedTemplates.push(...(customTemplatesByAnchor.get(systemTemplate.id) ?? []));
    }
    orderedTemplates.push(...unanchoredCustomTemplates);
    return orderedTemplates;
  }

  private showRecentlyUpdatedBadge(templateId: string): void {
    this.recentlyUpdatedTemplateId.set(templateId);
    if (this.#recentlyUpdatedBadgeTimeout) {
      clearTimeout(this.#recentlyUpdatedBadgeTimeout);
    }
    this.#recentlyUpdatedBadgeTimeout = setTimeout(() => {
      this.recentlyUpdatedTemplateId.set(null);
      this.#recentlyUpdatedBadgeTimeout = null;
    }, 3000);
  }

  private normalizeDropIndex(sourceIndex: number, targetIndex: number, total: number): number {
    const boundedTargetIndex = Math.max(0, Math.min(targetIndex, total));
    return sourceIndex < boundedTargetIndex ? boundedTargetIndex - 1 : boundedTargetIndex;
  }

  private moveTemplate(
    templates: DocumentTemplateWithSelected[],
    sourceIndex: number,
    targetIndex: number
  ): DocumentTemplateWithSelected[] {
    const updated = [...templates];
    const [movedTemplate] = updated.splice(sourceIndex, 1);
    updated.splice(targetIndex, 0, movedTemplate);
    return updated;
  }

  private hasLockedTemplatePositionChanged(
    previousTemplates: DocumentTemplateWithSelected[],
    nextTemplates: DocumentTemplateWithSelected[]
  ): boolean {
    const nextIndexById = new Map(nextTemplates.map((template, index) => [template.id, index]));
    return previousTemplates.some((template, previousIndex) => {
      if (!this.isTemplatePositionLocked(template)) {
        return false;
      }
      return nextIndexById.get(template.id) !== previousIndex;
    });
  }

  private openPreviewModal(name: string, url: string): void {
    this._selectedDocument.set({ name, url });
    const modalRef = this.#modalService.open(this.documentPreviewModal(), this.#ngbModalService.fullscreenModal);
    merge(modalRef.closed, modalRef.dismissed).subscribe(() => {
      this._selectedDocument.set(null);
    });
  }

  private resolveTemplatePreviewUrl(document: DocumentTemplate | miscellaneousDocument): string | null {
    const s3Document = document.s3Document as
      | (DocumentTemplate['s3Document'] & { directurl?: string; directURL?: string })
      | undefined;
    const documentWithAliases = document as DocumentTemplate & { directUrl?: string };
    return s3Document?.directUrl ?? documentWithAliases.directUrl ?? null;
  }

  private uploadFileToS3(file: File): Observable<{ documentId: string; s3Key: string }> {
    return this.#fileUploadService.getPrivateAWSSignedUrl(file).pipe(
      switchMap(awsData =>
        this.#fileUploadService.uploadFileToS3(awsData).pipe(
          filter((uploadEvent: any) => uploadEvent?.type === HttpEventType.Response),
          take(1),
          map(() => awsData)
        )
      )
    );
  }

  private extractFileFromInputEvent(event: Event): File | null {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    input.value = '';
    return file;
  }
}
